import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { suggestEmailFix } from '../lib/emailTypo';

/**
 * GuestUpgradeCard — shown on the More tab ONLY for guest sessions.
 * Converts the guest into a full account IN PLACE (same user id — team,
 * leagues and points survive) via POST /api/auth/guest/upgrade.
 *
 * 2026-08-27 — the account form is extracted as `GuestUpgradeDialog` so the
 * post-first-draft prompt (GuestFirstDraftPrompt) can reuse it.
 */

function formatDetail(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (detail.message) return detail.message;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || '').filter(Boolean).join(' ');
  return 'Something went wrong. Please try again.';
}

export function GuestUpgradeDialog({ open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const emailSuggestion = suggestEmailFix(email);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = { email: email.trim(), password };
      const name = username.trim();
      if (name && name !== user?.username) payload.username = name;
      await api.post('/auth/guest/upgrade', payload);
      await refreshUser?.();
      onOpenChange(false);
      toast.success('Account created — your team and progress are saved!');
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="guest-upgrade-dialog">
        <DialogHeader>
          <DialogTitle>Create your account</DialogTitle>
          <DialogDescription>
            Your current team, points and leagues carry over — nothing is lost.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-muted-foreground">Email</label>
            <Input
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="guest-upgrade-email-input"
            />
            {/* 2026-11-01 — same "…@gmailcom" catcher as the sign-up form. */}
            {emailSuggestion && (
              <button
                type="button"
                className="mt-1 text-left text-xs text-primary hover:underline underline-offset-2"
                onClick={() => setEmail(emailSuggestion)}
                data-testid="guest-upgrade-email-suggestion"
              >
                Did you mean <span className="font-semibold">{emailSuggestion}</span>?
              </button>
            )}
          </div>
          <div>
            <label className="text-[11px] font-semibold text-muted-foreground">Username</label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={user?.username || 'Username'}
              data-testid="guest-upgrade-username-input"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-muted-foreground">Password (8+ characters)</label>
            <Input
              type="password" required minLength={8} value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="guest-upgrade-password-input"
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="guest-upgrade-submit-btn">
            {busy ? 'Creating…' : 'Save my progress'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function GuestUpgradeCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user?.is_guest) return null;

  return (
    <div className="mb-6" data-testid="guest-upgrade-card">
      <div className="bg-gradient-to-r from-[hsl(var(--accent))]/15 via-card to-card border border-[hsl(var(--accent))]/40 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-[hsl(var(--accent))]/15 flex items-center justify-center shrink-0">
            <UserPlus className="h-4 w-4 text-[hsl(var(--accent))]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold">You're playing as a guest</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Guest progress lives only on this device — create a free account to keep
              your team, points and leagues forever (and sign in anywhere).
            </p>
          </div>
        </div>
        <Button
          size="sm"
          className="mt-3 w-full"
          onClick={() => setOpen(true)}
          data-testid="guest-upgrade-open-btn"
        >
          Create your free account
        </Button>
      </div>

      <GuestUpgradeDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
